import { PageFooter as SiteFooter } from '@components/pageFooter';
import { PageHeader } from '@components/pageHeader';
import { ArchivePage } from '@page/archive';
import { ContactPage } from '@page/conatactPage';
import { ContentPage } from '@page/contentPage';
import { CutOffListPage } from '@page/cutoffListPage';
import { DeniedPage } from '@page/deniedPage';
import { ErrorPage } from '@page/errorPage';
import { FrontPage } from '@page/frontPage';
import { FundsDetailsPage } from '@page/fundsDetailsPage';
import { FundsListPage } from '@page/fundsListPage';
import { InfoServicePage } from '@page/infoServicePage';
import { PublicationsPage } from '@page/publicationsPage';
import { SearchPage } from '@page/searchPage/SearchPage';
import { SectionPage } from '@page/sectionPage';
import { SimplePage } from '@page/simplePage';
import { VisitorInfoPage } from '@page/visitorInfoPage';
import { cn } from '@utils/helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import type { Site_site$key } from '../__generated__/Site_site.graphql';
import type { Site_user$key } from '../__generated__/Site_user.graphql';
import type { VisitorInfoType as VisitorInfoRouteType } from '../types/visitorInfo';
import { getStorageKey } from '../types/visitorInfo';
import type { ILocationDescriptor } from './FundsService';

interface VisitorInfoType {
  country: 'at' | 'de' | null;
  code: 'private' | 'business';
}

interface SiteProps {
  site: Site_site$key;
  user: Site_user$key;
  path?: string | string[];
  path2?: string;
  lang: string;
  location: ILocationDescriptor;
  history?: any;
  currentSalesAdmission?: string;
  onChangeSalesAdmission?: any;
  visitorInfo: VisitorInfoType | null;
  visitorInfoType: VisitorInfoRouteType;
  needsNavigation: boolean;
  /**
   * Called after the user confirms the visitor-info disclaimer. Lets the query
   * container re-read sessionStorage and re-fetch, so a page the server gated
   * behind visitor info (e.g. a directly-opened fund detail) loads correctly.
   */
  onVisitorInfoConfirmed?: () => void;
}

const TypeMap: Record<string, React.ComponentType<any>> = {
  FundsDetailsPage,
  FundsListPage,
  InfoServicePage,
  SimplePage,
  ArchivePage,
  CutOffListPage,
  FrontPage,
  ContactPage,
  SectionPage,
  ContentPage,
  SearchPage,
  DeniedPage,
  VisitorInfoPage,
  PublicationsPage,
};

// Main Site component using useFragment hooks
function Site(props: SiteProps) {
  const { site: siteKey, user: userKey, location, visitorInfoType, needsNavigation, onChangeSalesAdmission, visitorInfo: initialVisitorInfo, lang, onVisitorInfoConfirmed } = props;

  const navigate = useNavigate();

  // Use useFragment to read fragment data
  const site = useFragment(
    graphql`
            fragment Site_site on Site 
            @argumentDefinitions(
                path: {type: "String!"}
                visitorInfo: {type: "VisitorInfo"}
                language: {type: "String!"}
                salesAdmission: {type: "String!"}
                searchString: {type: "String"}
                specialPageId: {type: "SpecialPage!"}
            ) {
                page(path: $path, visitorInfo: $visitorInfo) {
                    id
                    pageTitle
                    header {
                        ...PageHeader_pageHeader
                    }
                    __typename
                    ...FundsDetailsPage_page @arguments(salesAdmission: $salesAdmission)
                    ...FundsListPage_page @arguments(salesAdmission: $salesAdmission)
                    ...InfoServicePage_page @arguments(salesAdmission: $salesAdmission)
                    ...SimplePage_page
                    ...ArchivePage_page @arguments(salesAdmission: $salesAdmission)
                    ...CutOffListPage_page
                    ...FrontPage_page
                    ...ContentPage_page
                    ...SectionPage_page
                    ...ContactPage_page
                    ...SearchPage_page @arguments(searchString: $searchString)
                    ...VisitorInfoPage_page
                    ...PublicationsPage_page
                    ...DeniedPage_page
                    translations {
                        language
                        url
                        ...PageHeader_translations
                    }
                }
                userCountry
                siteInfo(path: $path) {
                    ...PageHeader_siteInfo
                    ...PageFooter_siteFooter
                }
                specialPage(language: $language, pageId: $specialPageId) {
                    ...VisitorInfoPage_page
                    __typename
                }
            }
        `,
    siteKey,
  );

  const user = useFragment(
    graphql`
            fragment Site_user on UserContainer {
                isLoggedIn
            }
        `,
    userKey,
  );

  const transitionRef = useRef<HTMLDivElement>(null);

  // Local state tracks visitor info confirmation within the current session.
  // We use the prop as the source of truth for the gating decision, and only
  // use local state to trigger a re-render after the user confirms the disclaimer.
  const [confirmedVisitorInfo, setConfirmedVisitorInfo] = useState<VisitorInfoType | null>(null);
  const [smallHeader, setSmallHeader] = useState(false);

  // Reset confirmed state when the route type changes (e.g., funds → publications)
  // so the gating check uses the prop value for the new route.
  useEffect(() => {
    setConfirmedVisitorInfo(null);
  }, [visitorInfoType]);

  // The effective visitorInfo: use the freshly confirmed value if it exists
  // (user just confirmed on this route), otherwise fall back to the prop
  // (read from sessionStorage by SiteQueryContainer for this route's key).
  const visitorInfo = confirmedVisitorInfo ?? initialVisitorInfo;

  // Compute current language - memoized to avoid recalculation
  const currentLanguage = useMemo(() => {
    const langLabel = 'de-AT';

    if (!lang || !site || !site.page) {
      return langLabel;
    }

    const filteredTranslations = Object.values(site.page.translations || []).filter((valArr: any) => valArr.url.indexOf(`/${lang}/`) === 0)[0] as any;

    return filteredTranslations?.language || langLabel;
  }, [lang, site]);

  // Set document title on mount and when page changes
  useEffect(() => {
    document.title = site?.page ? site.page.pageTitle || 'Bank Gutmann' : 'Bank Gutmann';
    // Remove loading class
    document.body.classList.remove('is-loading');
  }, [site?.page]);

  // Handler for language change - memoized with useCallback
  const changeLanguage = useCallback(
    (key: string, url: string): void => {
      navigate(
        { pathname: url },
        {
          state: {
            ...location.state,
            language: key,
          },
        },
      );
    },
    [navigate, location],
  );

  // Handler for visitor info change - memoized with useCallback.
  // Update local state for an immediate gate flip, then notify the query
  // container so it re-fetches with the newly provided visitor info (the server
  // may return a different page now that access is granted).
  const setVisitorInfo = useCallback(
    (newVisitorInfo: VisitorInfoType): void => {
      setConfirmedVisitorInfo(newVisitorInfo);
      onVisitorInfoConfirmed?.();
    },
    [onVisitorInfoConfirmed],
  );

  // Early return for missing data
  if (!site || !site.page || !site.page.__typename || !site.siteInfo) {
    return <ErrorPage error={{ statusCode: 404 }} />;
  }

  const forceSmall = smallHeader;
  const isDeniedPage = site.page.__typename === 'DeniedPage';

  try {
    const pageTypeName = site.page.__typename;
    const Page = TypeMap[pageTypeName];

    // If page type not found in TypeMap, show error
    if (!Page) {
      // biome-ignore lint/suspicious/noConsole: This is an error message
      console.error(`[Site] Page type "${pageTypeName}" not found in TypeMap. Available types:`, Object.keys(TypeMap));
      return <ErrorPage error={{ statusCode: 501, message: `Page type "${pageTypeName}" not yet migrated to Relay Modern` }} />;
    }

    return (
      <div className="flow-root">
        <CSSTransition
          classNames="fade"
          nodeRef={transitionRef}
          addEndListener={(done: () => void) => {
            transitionRef.current?.addEventListener('animationend', done, false);
          }}
          in={true}
          appear={true}
        >
          <div ref={transitionRef}>
            <PageHeader
              changeLanguageFn={changeLanguage}
              currentLanguage={currentLanguage || 'de-AT'}
              pageHeader={site.page.header}
              siteInfo={site.siteInfo}
              translations={site.page.translations}
              needsNavigation={needsNavigation}
              onSmallHeaderChange={setSmallHeader}
            />
            <div className={cn('Page-wrapper shadow-md', forceSmall && 'pt-[186px] max-[700px]:pt-[120px]')}>
              {visitorInfo || visitorInfoType === 'none' ? (
                <Page
                  page={site.page}
                  visitorInfoPage={isDeniedPage ? site.specialPage : null}
                  location={location}
                  isLoggedIn={user.isLoggedIn}
                  currentLanguage={currentLanguage || 'de-AT'}
                  onChangeSalesAdmission={onChangeSalesAdmission}
                  salesAdmission={site.page.__typename !== 'InfoServicePage' ? visitorInfo?.country || 'AT' : 'AT'}
                  userCountry={site.userCountry}
                  searchString={location.query?.search}
                  visitorInfo={visitorInfo}
                  isRejected={site.page.__typename === 'VisitorInfoPage'}
                  setVisitorInfoFn={setVisitorInfo}
                />
              ) : (
                // key by disclaimer type so switching disclaimers (e.g. publications -> funds)
                // remounts a fresh VisitorInfoPage instead of reusing the reused route
                // instance, which would otherwise carry over transient state like `isRejected`.
                <VisitorInfoPage key={visitorInfoType} page={site.specialPage} setVisitorInfoFn={setVisitorInfo} visitorInfo={visitorInfo} isRejected={false} storageKey={getStorageKey(visitorInfoType) ?? undefined} />
              )}
            </div>
            <SiteFooter siteFooter={site.siteInfo} />
          </div>
        </CSSTransition>
      </div>
    );
  } catch (e) {
    return <ErrorPage error={{ statusCode: 500 }} />;
  }
}

export default Site;
